/**
 * Card for a partner.
 */
import * as React from 'react';
import { Building2, MapPin, Globe, Mail } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { PARTNER_TYPE_CONFIG } from './partner-configs';
import { stripProtocol } from './partner-table-utils';
import type { Partner } from '@/types/partner.types';

/** Props for {@link PartnerCard}. */
interface PartnerCardProps {
  partner: Partner;
  pageActions?: {
    canView?: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
  };
  onView?: (partner: Partner) => void;
  onEdit?: (partner: Partner) => void;
  onDelete?: (id: string) => void;
}

/** Card for a partner. */
export const PartnerCard: React.FC<PartnerCardProps> = ({
  partner,
  pageActions,
  onView,
  onEdit,
  onDelete,
}) => {
  const canView = pageActions?.canView ?? true;
  const canEdit = pageActions?.canEdit ?? true;
  const canDelete = pageActions?.canDelete ?? true;

  const typeConfig = PARTNER_TYPE_CONFIG;
  const TypeIcon = typeConfig?.icon;

  return (
    <TableCard onClick={() => onView?.(partner)}>
      <div className="data-card-header">
        <LabelBadge
          label={typeConfig?.label ?? 'Partenaire'}
          icon={TypeIcon}
          variant={typeConfig?.badgeVariant ?? 'ghost'}
          color={typeConfig?.badgeColor ?? 'var(--color-2-nuance-1)'}
          size="sm"
        />
      </div>

      <div className="data-card-content" style={{ overflow: 'hidden' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)] shrink-0">
            <Building2 className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-foreground text-sm truncate">{partner.name}</span>
            {partner.description && (
              <span className="text-xs text-muted-foreground truncate">{partner.description}</span>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          {partner.city && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <MapPin className="w-3.5 h-3.5 shrink-0 text-[var(--color-1-nuance-1)]" />
              <span className="truncate">
                {partner.city}
                {partner.department && ` (${partner.department})`}
              </span>
            </div>
          )}

          {partner.website && (
            <div className="flex items-center gap-2 text-xs">
              <Globe className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
              <a
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--color-2-nuance-1)] hover:underline truncate"
                onClick={(e) => e.stopPropagation()}
              >
                {stripProtocol(partner.website)}
              </a>
            </div>
          )}

          {partner.contactEmail && (
            <div className="flex items-center gap-2 text-xs">
              <Mail className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
              <a
                href={`mailto:${partner.contactEmail}`}
                className="text-foreground hover:text-[var(--color-2-nuance-1)] truncate"
                onClick={(e) => e.stopPropagation()}
              >
                {partner.contactEmail}
              </a>
            </div>
          )}
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          {canView && onView && (
            <Button
              preset="row-view"
              onClick={(e) => {
                e.stopPropagation();
                onView(partner);
              }}
            />
          )}
          {canEdit && onEdit && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(partner);
              }}
            />
          )}
          {canDelete && onDelete && (
            <Button
              preset="row-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(partner.id);
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
